import ErrorPage from "./error-page";
import WithBreadcrumb from "./components/WithBreadcrumb";
import Login from "./routes/Login";
import Register from "./routes/Register";
import ConsultantRegister from "./routes/ConsultantRegister";
import { Routes, Route } from "react-router-dom";
import { Navigate } from "react-router-dom";
import InstagramOAuthCallback from "./components/oauth/InstagramOAuthCallback";
import { AuthLayout } from "./layouts/Auth";
import MainLayout from "./layouts/Main";
import Dashboard from "./routes/Dashboard";
import Profile from "./routes/Profile";
import Settings from "./routes/Settings";
import TeamMembers from "./routes/settings/TeamMembers";
import Onboarding from "./routes/Onboarding";
import { useDispatch } from "react-redux";

import { useQuery } from "@apollo/client";
import { GET_CURRENT_USER_QUERY } from "../src/graphql/queries";
import Loader from "./components/views/Loader";
import SecuritySettings from "./routes/settings/Security";
import NotificationsSettings from "./routes/settings/Notifications";
import OrganisationsSettings from "./routes/settings/Organisations";
import BillingSettings from "./routes/settings/Billing";
import OutfitDetails from "./routes/outfits/OutfitDetails";
import AuditLogs from "./routes/settings/AuditLogs";
import EditOrganisation from "./routes/settings/organisations/Edit";
import { useEffect } from "react";
import Wardrobe from "./routes/wardrobe";
import Outfits from "@/routes/outfits";
import WardrobeNew from "@/routes/wardrobe/new";
import WardrobeItem from "@/routes/wardrobe/item";
import Home from "@/routes/Home";
import {
  EchoLensProvider,
  useEchoLens,
  SurveyRenderer,
} from "@echolens/sdk-react";

import { Toaster } from "react-hot-toast";
import { useAuth } from "./components/hooks/useAuth";

function EchoLensAuthSync({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { identify } = useEchoLens();

  useEffect(() => {
    if (user) {
      identify(user.id, {
        email: user.email,
        name: user.name,
      });
    }
  }, [user, identify]);

  return <>{children}</>;
}

const App = () => {
  const { data, loading, error } = useQuery(GET_CURRENT_USER_QUERY);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!loading && !error && data?.getCurrentUser) {
      dispatch({ type: "SET_USER_DETAILS", payload: data.getCurrentUser });
    }
  }, [loading, error, data, dispatch]);

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 5000,
          style: {
            background: "rgba(31, 41, 55, 0.95)",
            color: "#f9fafb",
            fontWeight: "200",
            letterSpacing: "0.15em",
            fontSize: "14px",
            textTransform: "uppercase",
            border: "1px solid rgba(156, 163, 175, 0.2)",
            borderRadius: "12px",
            backdropFilter: "blur(12px)",
            boxShadow:
              "0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.05)",
            padding: "16px 24px",
            minWidth: "320px",
            textAlign: "center",
          },
          success: {
            style: {
              background:
                "linear-gradient(135deg, rgba(139, 92, 246, 0.95) 0%, rgba(99, 102, 241, 0.95) 100%)",
              border: "1px solid rgba(139, 92, 246, 0.3)",
              boxShadow:
                "0 25px 50px -12px rgba(139, 92, 246, 0.25), 0 0 0 1px rgba(139, 92, 246, 0.1)",
            },
          },
          error: {
            style: {
              background:
                "linear-gradient(135deg, rgba(239, 68, 68, 0.95) 0%, rgba(220, 38, 38, 0.95) 100%)",
              border: "1px solid rgba(239, 68, 68, 0.3)",
              boxShadow:
                "0 25px 50px -12px rgba(239, 68, 68, 0.25), 0 0 0 1px rgba(239, 68, 68, 0.1)",
            },
          },
        }}
      />

      <Routes>
        {/* Public routes */}
        <Route path="/" element={<Home />} />

        {/* Auth routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/register/consultant" element={<ConsultantRegister />} />
        </Route>

        {/* App routes */}
        <Route element={<MainLayout />}>
          <Route
            path="/home"
            element={
              <WithBreadcrumb title="Dashboard">
                <Dashboard />
              </WithBreadcrumb>
            }
          />

          <Route path="/wardrobe" element={<Wardrobe />} />
          <Route path="/wardrobe/new" element={<WardrobeNew />} />
          <Route path="/wardrobe/item/:id" element={<WardrobeItem />} />

          <Route path="/profile" element={<Profile />} />
          <Route path="/lookbook" element={<Outfits />} />
          <Route
            path="/lookbook/:id"
            element={
              <WithBreadcrumb
                title="Look Details"
                trail={[{ label: "Lookbook", to: "/lookbook" }]}
              >
                <OutfitDetails />
              </WithBreadcrumb>
            }
          />

          {/* Temporary redirects for legacy routes */}
          <Route
            path="/outfits"
            element={<Navigate to="/lookbook" replace />}
          />
          <Route
            path="/outfits/:id"
            element={<Navigate to="/lookbook/:id" replace />}
          />

          <Route path="/settings" element={<Settings />} />
          <Route path="/settings/team-members" element={<TeamMembers />} />
          <Route path="/settings/security" element={<SecuritySettings />} />
          <Route
            path="/settings/notifications"
            element={<NotificationsSettings />}
          />
          <Route
            path="/settings/organisations"
            element={<OrganisationsSettings />}
          />
          <Route
            path="/settings/organisations/:id/edit"
            element={<EditOrganisation />}
          />
          <Route path="/settings/billing" element={<BillingSettings />} />
          <Route path="/settings/audit-logs" element={<AuditLogs />} />
        </Route>

        {/* Standalone routes */}
        <Route path="/onboarding" element={<Onboarding />} />
        <Route
          path="/oauth/instagram/callback"
          element={<InstagramOAuthCallback />}
        />
        <Route path="*" element={<ErrorPage />} />
      </Routes>
    </>
  );
};

function AppWithEchoLens() {
  return (
    <EchoLensProvider
      apiKey={import.meta.env.VITE_ECHOLENS_SDK_KEY || ""}
      appId={import.meta.env.VITE_ECHOLENS_APP_ID || ""}
      autocapture={true}
      debug={true}
    >
      <EchoLensAuthSync>
        <App />
        <SurveyRenderer />
      </EchoLensAuthSync>
    </EchoLensProvider>
  );
}

export default AppWithEchoLens;
